import { PUBLIC_BRAND_NAME } from './branding';
import api from '../services/api';
import { isCoachFoodOperator } from '../components/dashboard/roles';
import { getShellToken, setShellToken } from './shellToken';
import { hasPaidFoodAccess, hasPendingFoodAccess } from './foodAccess';

/**
 * FOOD PLAN dentro del ecosistema (d28d.app/food-plan).
 * Producto Nest independiente; entrada vía SSO shell. Correos siguen en Nest.
 * Fallback externo solo si VITE_FOOD_EXTERNAL=true (rollback).
 */

/** ¿Puede volver al shell sin cerrar sesión? Food-only → false. */
function shellUserCanReturnToEcosystem(user, { coachOperator = false, isPlatformAdmin = false } = {}) {
  if (coachOperator || isPlatformAdmin) return true;
  if (!user) return false;
  const ma = user.module_access && typeof user.module_access === 'object' ? user.module_access : {};
  if (ma.d28d || ma.live_classes || ma.gym || ma.training) return true;
  const licenses = Array.isArray(user.licenses) ? user.licenses : [];
  const active = licenses.filter((lic) => lic.active !== false);
  const codes = new Set(active.map((lic) => String(lic.module_code || '').toLowerCase()));
  if (['d28d', 'gym', 'live_classes', 'training'].some((c) => codes.has(c))) return true;
  // Solo FOOD (o sin módulos ecosistema): no mostrar «Volver al ecosistema».
  return false;
}

/** URL pública legacy (rollback / marketing). */
export const FOOD_PUBLIC_LOGIN_URL = String(
  import.meta.env.VITE_FOOD_PUBLIC_LOGIN_URL || 'https://foodplan.tech/login',
).replace(/\/$/, '');

/** @deprecated Legacy monolito OFF. */
export function isFoodLegacyMode() {
  return String(import.meta.env.VITE_FOOD_LEGACY || '').toLowerCase() === 'true';
}

/**
 * true = producto Nest (no FoodLog legacy del shell).
 * Con embed activo se abre /food-plan; con VITE_FOOD_EXTERNAL=true → URL pública.
 */
export function isFoodExternal() {
  return !isFoodLegacyMode();
}

/** ¿Forzar foodplan.tech en lugar de /food-plan? (rollback). */
export function isFoodPublicExternalOnly() {
  return String(import.meta.env.VITE_FOOD_EXTERNAL || '').toLowerCase() === 'true';
}

export function getFoodModulePublicUrl() {
  if (isFoodPublicExternalOnly()) {
    return String(import.meta.env.VITE_FOOD_MODULE_URL || 'https://foodplan.tech').replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/food`;
  }
  return String(import.meta.env.VITE_FOOD_MODULE_URL || 'https://foodplan.tech').replace(/\/$/, '');
}

export function getFoodPublicLoginUrl() {
  if (isFoodPublicExternalOnly()) return FOOD_PUBLIC_LOGIN_URL;
  return '/food/login';
}

/** Sin sesión shell: login ecosistema. Con sesión: abre Food embebido. */
export function redirectToFoodLogin(options = {}) {
  if (isFoodPublicExternalOnly()) {
    const target = FOOD_PUBLIC_LOGIN_URL;
    if (options.newTab && typeof window !== 'undefined') {
      window.open(target, '_blank', 'noopener,noreferrer');
      return { ok: true, redirecting: true, url: target };
    }
    if (typeof window !== 'undefined') window.location.href = target;
    return { ok: true, redirecting: true, url: target };
  }
  const shellToken = getShellToken();
  if (shellToken) {
    return openFoodModule('/dashboard', { ...options, newTab: options.newTab });
  }
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
  return { ok: true, redirecting: true, url: '/login' };
}

export const FOOD_LEGACY_VIEWS = new Set([
  'calculator',
  'admin',
  'foodlog',
  'recipes',
  'foodsmanager',
  'equivalentes',
  'myplan',
  'progress',
  'progress-food',
]);

export function isFoodLegacyView(viewId) {
  return FOOD_LEGACY_VIEWS.has(viewId);
}

export async function getFoodModuleStatus() {
  try {
    const { data } = await api.get('/food-module/status');
    return { ok: true, ...(data?.data || {}) };
  } catch (err) {
    return {
      ok: false,
      error: err?.response?.data?.error || err?.message || 'No se pudo verificar FOOD PLAN',
    };
  }
}

export function redirectToContratarFood() {
  try {
    sessionStorage.setItem('shell_nav_view', 'myaccount');
    sessionStorage.setItem('shell_food_upsell', '1');
  } catch { /* storage no disponible */ }
  if (typeof window !== 'undefined') window.location.href = '/';
}

export function isFoodSubscribeRequired(payload = {}) {
  if (payload?.redirectToPayment) return true;
  if (payload?.action === 'subscribe_food') return true;
  const code = payload?.code;
  return [
    'not_licensed',
    'food_not_licensed',
    'food_subscription_required',
    'food_not_provisioned',
    'food_account_unlinked',
  ].includes(code);
}

export function normalizeFoodConnectionError(errOrPayload) {
  const data = errOrPayload?.response?.data || errOrPayload || {};
  const raw = String(data.error || data.message || data.detail || errOrPayload?.error || errOrPayload?.message || '').trim();
  const code = data.code || errOrPayload?.code || null;
  if (code === 'food_schema_drift' || /shell_user_id|does not exist/i.test(raw)) {
    return {
      error: data.error
        || 'FOOD PLAN tiene desfase de BD (shell_user_id). En VPS: git pull && bash scripts/repair_foodplan_shell_columns.sh',
      code: 'food_schema_drift',
      action: 'retry',
      redirectToPayment: false,
      detail: data.detail || raw,
    };
  }
  if (code === 'food_trainer_conflict' || /conflicto al vincular perfil entrenador/i.test(raw)) {
    return {
      error: data.error || 'Conflicto al vincular perfil entrenador FOOD. Reintenta.',
      code: 'food_trainer_conflict',
      action: 'retry',
      redirectToPayment: false,
    };
  }
  if (/internal server error|sso food falló/i.test(raw) || code === 'food_sso_internal') {
    return {
      error: 'FOOD PLAN no pudo completar el SSO. Pulsa Reintentar; si persiste, contacta soporte.',
      code: 'food_sso_internal',
      action: 'retry',
      redirectToPayment: false,
      detail: data.detail || raw,
    };
  }
  if (/token sso ya utilizado|sso ya utilizado/i.test(raw) || code === 'food_sso_replay') {
    return {
      error: 'No se pudo completar el SSO. Pulsa Reintentar.',
      code: 'food_sso_replay',
      action: 'retry',
      redirectToPayment: false,
    };
  }
  if (/plan food ha vencido|renueva desde mi cuenta|tu plan ha vencido/i.test(raw)) {
    return {
      error: 'Tu plan FOOD PLAN venció. Renueva el pago desde Mi Cuenta (Wompi).',
      code: 'food_subscription_required',
      action: 'subscribe_food',
      redirectToPayment: true,
    };
  }
  if (/credenciales incorrectas/i.test(raw)) {
    return {
      error: 'Tu acceso FOOD PLAN no está activo. Contrátalo desde Mi Cuenta.',
      code: 'food_account_unlinked',
      action: 'subscribe_food',
      redirectToPayment: true,
    };
  }
  if (/food_external_public_login|foodplan\.tech/i.test(raw) || code === 'food_external_public_login') {
    return {
      error: 'FOOD PLAN embebido no está activo en el servidor.',
      code: 'food_external_public_login',
      action: 'open_food_login',
      redirectToPayment: false,
    };
  }
  return {
    error: raw || 'No se pudo conectar con FOOD PLAN.',
    code,
    action: data.action || errOrPayload?.action || null,
    redirectToPayment: isFoodSubscribeRequired(data) || isFoodSubscribeRequired(errOrPayload),
    detail: data.detail || undefined,
  };
}

export function getFoodUserDestPath(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [user?.rol].filter(Boolean);
  if (roles.includes('super_admin')) return '/superadmin';
  if (roles.some((r) => ['admin_food', 'admin_food_plan'].includes(r))) return '/admin';
  if (isCoachFoodOperator(user)) return '/trainer';
  return '/dashboard';
}

/**
 * Abre FOOD PLAN en /food-plan con SSO por sesión del shell.
 */
export async function openFoodModule(returnPath, options = {}) {
  if (isFoodLegacyMode()) {
    return { ok: true, legacy: true };
  }

  if (isFoodPublicExternalOnly()) {
    const target = FOOD_PUBLIC_LOGIN_URL;
    if (options.newTab && typeof window !== 'undefined') {
      window.open(target, '_blank', 'noopener,noreferrer');
    } else if (typeof window !== 'undefined') {
      window.location.href = target;
    }
    return { ok: true, redirecting: true, url: target };
  }

  const shellToken = getShellToken();
  if (!shellToken) {
    return { ok: false, error: 'Inicia sesión en el ecosistema antes de abrir FOOD PLAN.' };
  }

  const user = options.user || null;
  const coachOperator = isCoachFoodOperator(user);
  const roles = Array.isArray(user?.roles) ? user.roles : [user?.rol].filter(Boolean);
  const isPlatformAdmin = roles.includes('super_admin');

  if (!coachOperator) {
    try { sessionStorage.removeItem('d28d_food_coach_tracking'); } catch { /* noop */ }
  }

  const status = await getFoodModuleStatus();
  if (!status.ok && !coachOperator) {
    return { ok: false, error: status.error || 'FOOD PLAN no disponible en este momento.' };
  }
  if (status.mode === 'public_login_only' || status.code === 'food_external_public_login') {
    return {
      ok: false,
      error: 'FOOD PLAN embebido no está activo (FOOD_SHELL_EMBED_DISABLED).',
      code: 'food_external_public_login',
    };
  }
  if (status.enabled === false && !coachOperator) {
    return {
      ok: false,
      error: 'FOOD PLAN no está configurado en el servidor. Intenta más tarde o contacta soporte.',
      code: 'not_configured',
    };
  }

  const paidAccess = hasPaidFoodAccess(user, options.myServices);
  const pendingAccess = hasPendingFoodAccess(user, options.myServices);
  const canEnter = status.licensed === true || status.can_enter === true || paidAccess || pendingAccess || isPlatformAdmin || coachOperator;
  if (!canEnter) {
    redirectToContratarFood();
    return {
      ok: false,
      redirecting: true,
      code: 'not_licensed',
      redirectToPayment: true,
      action: 'subscribe_food',
    };
  }

  setShellToken(shellToken);
  localStorage.setItem('d28d_shell', 'true');
  localStorage.setItem('d28d_shell_label', import.meta.env.VITE_BRAND_NAME || PUBLIC_BRAND_NAME);

  const standalone = options.standalone === true
    || user?.entry_host === 'food'
    || (!coachOperator && !isPlatformAdmin);
  if (standalone) {
    try {
      const destRaw = pendingAccess
        ? '/pendiente-activacion'
        : (returnPath || getFoodUserDestPath(user));
      const dest = destRaw.startsWith('/') ? destRaw : `/${destRaw}`;
      const { data } = await api.get('/food-module/launch', {
        params: { dest, standalone: 1 },
      });
      const url = data?.data?.url;
      if (!url) {
        return { ok: false, error: 'No se recibió URL de Food Plan.' };
      }
      if (options.newTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
      return { ok: true, redirecting: true, url, mode: 'standalone' };
    } catch (e) {
      return {
        ok: false,
        error: e?.response?.data?.error || e?.message || 'No se pudo abrir Food Plan',
      };
    }
  }

  // Flag para «Volver al ecosistema» en Food (no aplica food-only).
  try {
    if (shellUserCanReturnToEcosystem(user, { coachOperator, isPlatformAdmin })) {
      sessionStorage.setItem('shell_can_return_ecosystem', '1');
    } else {
      sessionStorage.removeItem('shell_can_return_ecosystem');
    }
  } catch { /* noop */ }

  const params = new URLSearchParams();
  params.set('relaunch', '1');
  const destRaw = pendingAccess
    ? '/pendiente-activacion'
    : (returnPath || getFoodUserDestPath(user));
  const dest = destRaw.startsWith('/') ? destRaw : `/${destRaw}`;
  params.set('dest', dest);
  if (coachOperator && (dest === '/trainer' || options.coach)) {
    params.set('coach', '1');
  }

  const url = `/food/shell-sso?${params.toString()}`;
  if (options.newTab && typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else if (typeof window !== 'undefined') {
    window.location.href = url;
  }
  return { ok: true, redirecting: true, url };
}

/** @deprecated */
export function openFoodModulePublicFallback() {
  redirectToFoodLogin();
}

export function showFoodPlanMarketingLink() {
  return true;
}

export function foodPlanHasShellLicense(user, myServices) {
  return hasPaidFoodAccess(user, myServices);
}

void PUBLIC_BRAND_NAME;
